<script>
import store from "../../public/store/store";
import SectionToc from "./SectionToc.vue";
import PublicTagList from "./PublicTagList.vue";

export default {
    name: "SectionItem",
    components: { SectionToc, PublicTagList },
    props: ['itemId'],
    data() {
        return {
            item: {},
            loading: true,
            message: 'Загрузка...',
        }
    },
    computed: {
        itemHasToc() {
            return this.hasToc(this.item?.text || '');
        },
    },
    methods: {
        hasToc(content) {
            if (!content) {
                return false;
            }

            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = content;

            return Boolean(tempDiv.querySelector('nav.toc'));
        },
    },
    created() {
        const hash = this.$route.query.hash || this.$route.params.hash;
        store.commit('setCurrentHash', hash);
        let data = {
            hash: hash,
            itemId: this.itemId,
        };

        axios.post(`/rest/share/section/item`, data).then((response) => {
            this.item = response.data.item;
            this.loading = false;
        }).catch((error) => {
            if (error.response.status === 404) {
                this.message = 'Элемент не найден.';
            }
        });
    },
}
</script>

<template>
    <div class="section-item-container">
        <div v-if="loading === false" class="section-item-content">
            <div
                class="main-content-wrapper"
                :class="{ 'main-content-wrapper--full': !itemHasToc }"
            >
                <div class="content-body">
                    <div class="item-meta">
                        <span class="created_at">Дата создания: {{ item.created_at }}</span>
                    </div>
                    <div class="item-title">
                        <h1>{{ item.name }}</h1>
                    </div>
                    <public-tag-list :tags="item.tags" @select="$root.$emit('public-tag-search', $event)"></public-tag-list>
                    <div v-if="item.description" class="description" v-html="item.description"></div>
                    <div class="item-body froala-content" v-html="item.text"></div>
                </div>
                <aside v-if="itemHasToc" class="toc-sidebar">
                    <section-toc :content="item.text"></section-toc>
                </aside>
            </div>
        </div>
        <div v-else class="loading-state">
            <div class="loading-spinner">
                <svg class="spinner" viewBox="0 0 50 50">
                    <circle class="spinner-circle" cx="25" cy="25" r="20" fill="none" stroke-width="4"></circle>
                </svg>
                <span class="loading-text">{{ message }}</span>
            </div>
        </div>
    </div>
</template>

<style scoped>
.section-item-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
}

.section-item-content {
    /* Background and shadow moved to .content-body */
}

.main-content-wrapper {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 40px;
    padding: 30px;
    max-width: 1200px;
    margin: 0 auto;
}

.main-content-wrapper--full {
    grid-template-columns: minmax(0, 1fr);
    max-width: 100%;
}

.content-body {
    min-width: 0;
    overflow: hidden;
    max-width: 100%;
    background: #ffffff;
    border-radius: 8px;
    padding: 16px 30px 30px 30px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.item-meta {
    margin-top: 0;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #e5e7eb;
}

.created_at {
    font-size: 14px;
    color: #6b7280;
}

.item-title {
    margin-bottom: 25px;
}

.item-title h1 {
    font-size: 28px;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.3;
}

.item-body {
    font-size: 16px;
    line-height: 1.7;
    color: #374151;
}

.toc-sidebar {
    position: relative;
}

/* Froala editor content styles */
.froala-content :deep(h1),
.froala-content :deep(h2),
.froala-content :deep(h3),
.froala-content :deep(h4),
.froala-content :deep(h5),
.froala-content :deep(h6) {
    margin-top: 24px;
    margin-bottom: 16px;
    font-weight: 600;
    line-height: 1.25;
    color: #1f2937;
    scroll-margin-top: 100px;
    scroll-snap-margin-top: 100px;
}

.froala-content :deep(h1) {
    font-size: 24px;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 8px;
}

.froala-content :deep(h2) {
    font-size: 20px;
}

.froala-content :deep(h3) {
    font-size: 18px;
}

.froala-content :deep(p) {
    margin-bottom: 16px;
}

.froala-content :deep(ul),
.froala-content :deep(ol) {
    margin-bottom: 16px;
    padding-left: 24px;
}

.froala-content :deep(li) {
    margin-bottom: 8px;
}

.froala-content :deep(a) {
    color: #3b82f6;
    text-decoration: underline;
    transition: color 0.2s;
}

.froala-content :deep(a:hover) {
    color: #1d4ed8;
}

.froala-content :deep(img) {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 16px auto;
    display: block;
    cursor: pointer;
}

/* Froala image alignment classes */
.froala-content :deep(img.fr-dib) {
    display: block;
    width: 100%;
    max-width: 100%;
}

/* Image alignment - specific classes override fr-fic */
.froala-content :deep(img.fr-fil) {
    float: left;
    width: auto;
    max-width: 100%;
    margin: 0 16px 16px 0;
}

.froala-content :deep(img.fr-fir) {
    float: right;
    width: auto;
    max-width: 100%;
    margin: 0 0 16px 16px;
}

.froala-content :deep(img.fr-fic:not(.fr-fil):not(.fr-fir)) {
    display: block;
    width: 100%;
    max-width: 100%;
    margin: 16px auto;
    float: none;
}

.froala-content :deep(.fr-video) {
    max-width: 100%;
    overflow: hidden;
}

.froala-content :deep(.fr-video iframe),
.froala-content :deep(.fr-video video),
.froala-content :deep(iframe),
.froala-content :deep(video) {
    display: block;
    max-width: 100% !important;
}

.froala-content :deep(video) {
    width: 100% !important;
    height: auto !important;
}

/* Clearfix for all paragraphs in froala content */
.froala-content :deep(p::after) {
    content: "";
    display: table;
    clear: both;
}

.froala-content :deep(pre) {
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 16px;
    overflow-x: auto;
    margin-bottom: 16px;
    font-family: 'Courier New', Courier, monospace;
    font-size: 14px;
}

.froala-content :deep(code) {
    background: #f3f4f6;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', Courier, monospace;
    font-size: 14px;
}

.froala-content :deep(pre code) {
    background: none;
    padding: 0;
}

.froala-content :deep(table) {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 16px;
}

.froala-content :deep(th),
.froala-content :deep(td) {
    border: 1px solid #e5e7eb;
    padding: 12px;
    text-align: left;
}

.froala-content :deep(th) {
    background: #f9fafb;
    font-weight: 600;
}

.froala-content :deep(blockquote) {
    border-left: 4px solid #3b82f6;
    padding-left: 16px;
    margin: 16px 0;
    color: #4b5563;
    font-style: italic;
}

/* Hide TOC in the main content */
.froala-content :deep(nav.toc) {
    display: none;
}

@-moz-document url-prefix() {
    .froala-content :deep(table),
    .froala-content :deep(table *) {
        -moz-user-select: text !important;
        user-select: text !important;
    }

    .froala-content :deep(.fr-table-selected),
    .froala-content :deep(.fr-table-selected *) {
        -moz-user-select: text !important;
        caret-color: auto !important;
        user-select: text !important;
    }

    .froala-content :deep(.fr-table-selected *::-moz-selection) {
        background: rgba(25, 118, 210, .35) !important;
        color: inherit !important;
    }
}

.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    padding: 40px;
}

.loading-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
}

.spinner {
    width: 50px;
    height: 50px;
    animation: spin 1s linear infinite;
}

.spinner-circle {
    stroke: #3b82f6;
    stroke-linecap: round;
    stroke-dasharray: 90, 120;
}

.loading-text {
    font-size: 16px;
    color: #6b7280;
    font-weight: 500;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* Responsive styles */
@media (max-width: 1200px) {
    .main-content-wrapper {
        grid-template-columns: 1fr;
        gap: 20px;
    }

    .toc-sidebar {
        display: none;
    }
}

@media (max-width: 768px) {
    .section-item-container {
        padding: 16px;
    }

    .main-content-wrapper {
        padding: 20px;
    }

    .item-title h1 {
        font-size: 24px;
    }
}
</style>
